import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Building2, ChevronDown } from 'lucide-react'
import { isNil } from 'lodash'

import classes from './HeaderEmployerSelector.module.scss'

const StaticEmployerPill = ({ name }) => (
  <div className={`${classes.pill} header-control-surface`} data-testid="employer-pill">
    <Building2 aria-hidden className={classes.icon} />
    <span className={classes.name} title={name}>
      {name}
    </span>
  </div>
)

StaticEmployerPill.propTypes = {
  name: PropTypes.string.isRequired,
}

/**
 * Employer picker for multi-employer employees.
 * Matches employer-experience EmployerSwitcher pill chrome:
 * - 0 options → static pill with fallback company name (or null)
 * - 1 option → static pill (building + name)
 * - 2+ → pill trigger + dropdown list
 */
export const HeaderEmployerSelector = ({
  memberships = [],
  currentPersonId,
  companyNameFallback = '',
  onChange,
  disabled = false,
}) => {
  const list = memberships.map((m) => ({
    id: m.personId,
    name: m.companyName || 'Unknown',
  }))
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (disabled) {
      setOpen(false)
    }
  }, [disabled])

  useEffect(() => {
    if (!open) return undefined

    const onOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('click', onOutsideClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onOutsideClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (list.length === 0) {
    return companyNameFallback ? <StaticEmployerPill name={companyNameFallback} /> : null
  }

  if (list.length === 1) {
    return <StaticEmployerPill name={list[0].name} />
  }

  const selectedId = isNil(currentPersonId) ? list[0].id : currentPersonId
  const selected = list.find((item) => item.id === selectedId) || list[0]

  const handleSelect = (personId) => {
    setOpen(false)
    if (disabled) return
    if (!isNil(personId) && personId !== currentPersonId) {
      onChange?.(personId)
    }
  }

  return (
    <div className={classes.root} ref={rootRef}>
      <button
        type="button"
        className={`${classes.pillButton} header-control-surface`}
        aria-label="Switch active company"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={(event) => {
          if (disabled) return
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
      >
        <Building2 aria-hidden className={classes.icon} />
        <span className={classes.name} title={selected.name}>
          {selected.name}
        </span>
        <ChevronDown aria-hidden className={classes.icon} />
      </button>

      {open && (
        <div className={classes.menu} role="listbox" aria-label="Companies">
          <div className={classes.menuLabel}>Switch company ({list.length})</div>
          <div className={classes.menuSeparator} />
          {list.map((item) => {
            const isSelected = item.id === selected.id
            return (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={isSelected ? classes.menuItemSelected : classes.menuItem}
                onClick={() => handleSelect(item.id)}
              >
                {item.name}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

HeaderEmployerSelector.propTypes = {
  memberships: PropTypes.arrayOf(
    PropTypes.shape({
      personId: PropTypes.string.isRequired,
      companyName: PropTypes.string,
    })
  ),
  currentPersonId: PropTypes.string,
  companyNameFallback: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
}

HeaderEmployerSelector.defaultProps = {
  memberships: [],
  currentPersonId: '',
  companyNameFallback: '',
  onChange: () => {},
  disabled: false,
}

export default HeaderEmployerSelector
